import { useEffect } from "react";

/**
 * useSeo — sets document title, core meta tags, canonical URL, and JSON-LD
 * structured data for the current page.
 *
 * Lightweight alternative to react-helmet — no dependency, just DOM edits.
 * The JSON-LD block is what unlocks Google's rich result cards (offer/coupon
 * markup shows discount %, expiry, code preview directly in search results).
 */
export default function useSeo({ title, description, image, canonical, jsonLd, keywords }) {
  useEffect(() => {
    if (title) document.title = title;

    const set = (selector, attr, value) => {
      if (!value) return;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        selector
          .replace(/^meta\[/, "")
          .replace(/\]$/, "")
          .split("][")
          .forEach((pair) => {
            const [k, v] = pair.replace(/["']/g, "").split("=");
            if (k && v) tag.setAttribute(k, v);
          });
        document.head.appendChild(tag);
      }
      tag.setAttribute(attr, value);
    };

    if (description) {
      set('meta[name="description"]', "content", description);
      set('meta[property="og:description"]', "content", description);
      set('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      set('meta[property="og:title"]', "content", title);
      set('meta[name="twitter:title"]', "content", title);
    }
    if (image) {
      set('meta[property="og:image"]', "content", image);
      set('meta[name="twitter:image"]', "content", image);
      set('meta[name="twitter:card"]', "content", "summary_large_image");
    }
    if (keywords) {
      set('meta[name="keywords"]', "content", keywords);
    }
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      set('meta[property="og:url"]', "content", canonical);
    }
    // JSON-LD structured data. We tag the injected script with a data-attr so
    // repeat renders replace the block rather than accumulate multiple copies.
    if (jsonLd) {
      const tagId = "page-jsonld";
      let el = document.head.querySelector(`script[data-seo="${tagId}"]`);
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.setAttribute("data-seo", tagId);
        document.head.appendChild(el);
      }
      const payload = Array.isArray(jsonLd) ? { "@context": "https://schema.org", "@graph": jsonLd } : jsonLd;
      el.textContent = JSON.stringify(payload);
    }

    return () => {
      // Only clean the JSON-LD (keeps title/description for browser history back-nav)
      const el = document.head.querySelector('script[data-seo="page-jsonld"]');
      if (el) el.remove();
    };
  }, [title, description, image, canonical, jsonLd, keywords]);
}
